<template>
  <FullScreenModal :open="open" @close="closeModal">
    <div>
      <h1 class="flex items-center justify-center text-lg sm:text-2xl text-center font-bold">Ajout du devis</h1>
      <CompensationValueAlert
        v-if="currentClaim.compensationValue"
        :compensation-value="currentClaim.compensationValue"
        class="mt-3"
      />
      <p class="mt-2 sm:mt-4 text-sm text-gray-600">
        Détaillez les coûts et déposez un devis proforma pour que nos équipes puissent traiter ce sinistre.
      </p>
    </div>
    <div class="mt-5 sm:mt-7 border-t border-gray-200 pt-5 sm:pt-7">
      <p class="text-sm font-semibold text-gray-900">L'état du produit est-il conforme à la déclaration du client ?</p>
      <div class="flex flex-col sm:flex-row mt-3 gap-3">
        <label
          :class="[
            'flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors text-sm',
            conform === true
              ? 'border-primary bg-primary-light ring-1 ring-primary'
              : 'border-gray-200 hover:bg-gray-50',
          ]"
        >
          <input
            name="conform"
            type="radio"
            class="h-4 w-4 border-gray-300 text-primary-dark focus:ring-primary"
            :value="true"
            v-model="conform"
          />
          <span>Oui, conforme</span>
        </label>
        <label
          :class="[
            'flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors text-sm',
            conform === false
              ? 'border-primary bg-primary-light ring-1 ring-primary'
              : 'border-gray-200 hover:bg-gray-50',
          ]"
        >
          <input
            name="conform"
            type="radio"
            class="h-4 w-4 border-gray-300 text-primary-dark focus:ring-primary"
            :value="false"
            v-model="conform"
          />
          <span>Non, non conforme</span>
        </label>
      </div>
    </div>
    <div v-if="conform === true" class="mt-5 sm:mt-7 border-t border-gray-200 pt-5 sm:pt-7">
      <p class="text-sm font-semibold text-gray-900">Le produit est-il réparable ?</p>
      <div class="flex flex-col sm:flex-row mt-3 gap-3">
        <label
          :class="[
            'flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors text-sm',
            isProductFixable === true
              ? 'border-primary bg-primary-light ring-1 ring-primary'
              : 'border-gray-200 hover:bg-gray-50',
          ]"
        >
          <input
            name="product-fixable"
            type="radio"
            class="h-4 w-4 border-gray-300 text-primary-dark focus:ring-primary"
            :value="true"
            v-model="isProductFixable"
          />
          <span>Oui, le produit est réparable</span>
        </label>
        <label
          :class="[
            'flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors text-sm',
            isProductFixable === false
              ? 'border-primary bg-primary-light ring-1 ring-primary'
              : 'border-gray-200 hover:bg-gray-50',
          ]"
        >
          <input
            name="product-fixable"
            type="radio"
            class="h-4 w-4 border-gray-300 text-primary-dark focus:ring-primary"
            :value="false"
            v-model="isProductFixable"
          />
          <span>Non, le produit n'est pas réparable</span>
        </label>
      </div>
    </div>
    <div v-if="conform === false" class="border-t border-gray-200 pt-5 sm:pt-7 mt-5 sm:mt-7">
      <FormField
        v-model="nonConformityReason"
        type="text"
        label="Raison de la non-conformité"
        name="nonConformityReason"
        id="nonConformityReason"
        required
      />
    </div>
    <div v-if="isProductFixable === true" class="border-t border-gray-200 pt-5 sm:pt-7 mt-5 sm:mt-7">
      <FixeableProductProcess
        :extract-cost-items="extractCostItemsFromFile"
        @update="
          (newProductState, newFile, newCostItems) => {
            productState = newProductState;
            file = newFile;
            costItems = newCostItems;
          }
        "
      />
      <UploadApprovalAlerts
        class="mt-3"
        :file="file"
        document-kind="quotation"
        @update:analyzing="analyzing = $event"
      />
    </div>
    <div v-if="isProductFixable === false" class="border-t border-gray-200 pt-5 sm:pt-7 mt-5 sm:mt-7">
      <FormField
        v-model="productNotFixableReason"
        type="text"
        label="Raison de l'irréparabilité"
        name="nonFixableReason"
        id="nonFixableReason"
        required
      />
      <FormField
        class="mt-4"
        id="non-repairability-certificate"
        v-model="irreparabilityCertificate"
        required
        type="file"
        label="Attestation d'irréparabilité"
        field-class="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-hidden dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
        name="non-repairability-certificate"
      />
    </div>
    <PricingDetails :claim="currentClaim" :costItems="costItems" />
    <div v-if="validForm" class="mt-7 flex justify-center text-sm">
      <GenericButton :disabled="analyzing" @click="send">Valider et envoyer</GenericButton>
    </div>
  </FullScreenModal>
</template>

<script>
import {
  FullScreenModal,
  GenericButton,
  PricingDetails,
  FixeableProductProcess,
  CompensationValueAlert,
  FormField,
  isPresent,
} from "@estaly/ui";
import { mapActions, mapState } from "vuex";
import UploadApprovalAlerts from "@/components/claim/details/invoice/UploadApprovalAlerts.vue";
export default {
  props: ["open"],
  components: {
    FormField,
    CompensationValueAlert,
    PricingDetails,
    FixeableProductProcess,
    GenericButton,
    FullScreenModal,
    UploadApprovalAlerts,
  },
  computed: {
    ...mapState("claims", ["currentClaim"]),

    validForm() {
      if (this.conform === null) return false;

      if (this.conform) {
        const productFixableInformationsValid =
          this.isProductFixable && isPresent(this.file) && isPresent(this.costItems) && isPresent(this.productState);
        const productNonFixableInformationsValid =
          !this.isProductFixable &&
          isPresent(this.productNotFixableReason) &&
          isPresent(this.irreparabilityCertificate);

        if (productFixableInformationsValid || productNonFixableInformationsValid) return true;
      } else {
        return this.nonConformityReason !== null;
      }

      return false;
    },
  },
  data: () => {
    return {
      file: null,
      costItems: null,
      isProductFixable: null,
      productState: null,
      productNotFixableReason: null,
      conform: null,
      nonConformityReason: null,
      irreparabilityCertificate: null,
      analyzing: false,
    };
  },
  watch: {
    conform(newValue) {
      if (newValue === true) {
        this.productNotFixableReason = null;
        this.nonConformityReason = null;
        this.irreparabilityCertificate = null;
        this.file = null;
        this.costItems = null;
        this.isProductFixable = null;
        this.productState = null;
      } else if (newValue === false) {
        this.file = null;
        this.costItems = null;
        this.isProductFixable = null;
        this.productState = null;
      }
    },
    isProductFixable(newValue) {
      if (newValue === true) {
        this.productNotFixableReason = null;
        this.irreparabilityCertificate = null;
      } else if (newValue === false) {
        this.file = null;
        this.costItems = null;
        this.productState = null;
      }
    },
  },
  methods: {
    ...mapActions("claims", ["uploadQuotation", "declareIrreparable", "extractCostItems"]),
    ...mapActions("notifications", ["notify"]),
    async extractCostItemsFromFile(file) {
      return await this.extractCostItems({ file });
    },
    async send() {
      if (!this.conform) {
        const { success } = await this.declareIrreparable({
          message: "Déclaration non conforme",
          reason: this.nonConformityReason,
        });
        if (success) this.closeModal();
      } else if (this.isProductFixable) {
        const { success } = await this.uploadQuotation({
          costItems: this.costItems,
          file: this.file,
          productState: this.productState,
        });
        if (success) this.closeModal();
      } else {
        const { success } = await this.declareIrreparable({
          message: "Produit non réparable",
          irreparabilityCertificate: this.irreparabilityCertificate,
          reason: this.productNotFixableReason,
        });
        if (success) this.closeModal();
      }
    },
    closeModal() {
      this.$emit("close");
      this.file = null;
      this.costItems = null;
      this.isProductFixable = null;
      this.productNotFixableReason = null;
      this.irreparabilityCertificate = null;
      this.conform = null;
      this.nonConformityReason = null;
    },
  },
};
</script>
